import { selectedMusic, itemToMusicTime, musicToItemTime } from '../core/project.js';
import { tempoClock } from '../core/notation.js';

export function practiceBeatClock(item, fallbackBpm = 120) {
  const music = selectedMusic(item);
  const clock = tempoClock(music?.tempos?.length ? music.tempos : [{ time: 0, bpm: fallbackBpm }]);
  return music?.tempos?.length ? {
    beat: time => clock.beat(itemToMusicTime(item, time, music)),
    seconds: beat => musicToItemTime(item, clock.seconds(beat), music),
  } : clock;
}

// Schedule a short window against the audio clock, always reading the live
// transport so seeks, loops and speed changes cannot leave a second clock running.
export class PracticeMetronome {
  constructor(audio, getState, getItem, { schedule = callback => setInterval(callback, 25), unschedule = id => globalThis.clearInterval(id) } = {}) {
    Object.assign(this, { audio, getState, getItem, schedule, unschedule });
    this.enabled = false; this.sound = 'metronome'; this.bpm = 120; this.volume = 70;
  }
  async configure(settings) {
    const config = [!!settings.metronome, settings.metronomeSound ?? 'metronome', settings.countdownBpm ?? 120, settings.rhythmVolume ?? 70];
    if (JSON.stringify(config) === this.signature) return;
    this.stop(); this.signature = JSON.stringify(config);
    this.context = null;
    [this.enabled, this.sound, this.bpm, this.volume] = config;
    if (this.enabled && !this.getState().paused) await this.prepare();
  }
  async prepare() {
    if (!this.enabled || this.volume <= 0) return;
    const signature = this.signature, context = await this.audio.prepare(this.sound);
    if (signature !== this.signature) return;
    this.context = context; this.sync();
  }
  sync(event) {
    if (event === 'waiting' || event === 'stalled') this.waiting = true;
    if (['play', 'playing', 'pause'].includes(event)) this.waiting = false;
    if (['seeked', 'seeking', 'ratechange', 'waiting', 'ended', 'pause'].includes(event)) this.stop();
    const state = this.getState();
    if (!this.enabled || this.volume <= 0 || !this.getItem() || state.paused || state.seeking || this.waiting || this.context?.state !== 'running' || this.context !== this.audio.music.context) { this.stop(); return; }
    if (this.timer !== undefined) return;
    this.clock = practiceBeatClock(this.getItem(), this.bpm);
    this.nextBeat = Math.ceil(this.clock.beat(state.time - .03 * state.rate) - .0001);
    this.timer = this.schedule(() => this.tick()); this.tick();
  }
  tick() {
    const state = this.getState();
    if (!this.enabled || state.paused || state.seeking || this.waiting || this.context?.state !== 'running' || this.context !== this.audio.music.context) { this.stop(); return; }
    const horizon = state.time + .1 * state.rate;
    // Drop missed beats after a stalled tab instead of playing a burst of clicks.
    this.nextBeat = Math.max(this.nextBeat, Math.ceil(this.clock.beat(state.time - .03 * state.rate)));
    for (let count = 0; count < 32; count++) {
      const time = this.clock.seconds(this.nextBeat);
      if (time > horizon || time >= state.duration) break;
      this.audio.play(this.context, this.sound, this.context.currentTime + Math.max(0, (time - state.time) / state.rate), this.volume, 'metronome');
      this.nextBeat++;
    }
  }
  stop() {
    if (this.timer !== undefined) this.unschedule(this.timer);
    this.timer = undefined; this.audio.stop('metronome');
  }
}
